import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { LogoEmblem, BRAND_SLOGAN } from "./Logo";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-navy text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-navy opacity-40 pointer-events-none" />

      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <LogoEmblem size={68} variant="primary" />
              <div>
                <div className="font-display text-xl tracking-tight">
                  Destineze
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/60">
                  Educational Voyages FZCO
                </div>
              </div>
            </div>
            <div
              data-testid="footer-slogan"
              className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 border border-gold/30 bg-white/[0.03] text-[10px] uppercase tracking-[0.28em] text-gold"
            >
              <span aria-hidden="true">✦</span>
              {BRAND_SLOGAN}
            </div>
            <p className="mt-6 text-white/70 text-sm leading-relaxed max-w-md">
              A UAE-based global education and institutional solutions company
              building digital ecosystems, hosting world-class academic events,
              and engineering structured global exposure for institutions.
            </p>

            <div className="mt-8 space-y-3 text-sm text-white/70">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-1 text-gold" />
                <span data-testid="footer-address">
                  IFZA Business Park, Dubai Silicon Oasis, Dubai, UAE
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gold" />
                <a
                  href="mailto:info@destineze.com"
                  data-testid="footer-email"
                  className="hover:text-gold transition"
                >
                  info@destineze.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-gold" />
                <a
                  href="https://wa.me/971504883656"
                  data-testid="footer-whatsapp"
                  className="hover:text-gold transition"
                >
                  +971 50 488 3656
                </a>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-5">
              Solutions
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <Link
                  to="/school-os"
                  data-testid="footer-link-school-os"
                  className="text-white/80 hover:text-gold transition"
                >
                  School OS
                </Link>
              </li>
              <li>
                <Link
                  to="/event-os"
                  className="text-white/80 hover:text-gold transition"
                >
                  Event & MUN OS
                </Link>
              </li>
              <li>
                <Link
                  to="/diplomexa"
                  data-testid="footer-link-diplomexa"
                  className="text-white/80 hover:text-gold transition"
                >
                  Diplomexa Journeys
                </Link>
              </li>
              <li>
                <Link
                  to="/educational-tourism"
                  className="text-white/80 hover:text-gold transition"
                >
                  Educational Tourism
                </Link>
              </li>
              <li>
                <Link
                  to="/corporate-travel"
                  className="text-white/80 hover:text-gold transition"
                >
                  Institutional Travel
                </Link>
              </li>
              <li>
                <Link
                  to="/destination-management"
                  className="text-white/80 hover:text-gold transition"
                >
                  Destination Management
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-5">
              Company
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/about" className="text-white/80 hover:text-gold transition">
                  About
                </Link>
              </li>
              <li>
                <Link to="/partners" className="text-white/80 hover:text-gold transition">
                  Partner With Us
                </Link>
              </li>
              <li>
                <Link to="/insights" className="text-white/80 hover:text-gold transition">
                  Insights
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-white/80 hover:text-gold transition">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/80 hover:text-gold transition">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-5">
              Engage
            </div>
            <Link
              to="/contact"
              data-testid="footer-cta-partner"
              className="inline-flex items-center gap-2 group text-white hover:text-gold transition"
            >
              Become a global partner
              <ArrowUpRight
                size={16}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition"
              />
            </Link>
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="block mt-4 text-xs text-white/40 hover:text-white/70 transition"
            >
              Admin Console
            </Link>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-white/50">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} Destineze Educational Voyages FZCO.
            All rights reserved.
          </div>
          <div className="uppercase tracking-[0.2em]">
            UAE · Global Reach
          </div>
        </div>
      </div>
    </footer>
  );
}
